import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { ObjectShape } from 'yup/lib/object';
import { env } from 'constants/env';
import {
  StepType,
  useApplyProvider,
  useFields,
  useUpdateEntityStatus,
} from 'V2/Apply';
import { Spinner } from 'V2/Shared/Spinner';
import { Nullable, OptionUriType, SignatureSummaryType } from 'V2/Types';
import { useApi } from 'V2/Api';
import { ErrorBannerRed } from 'V2/Shared/ErrorBannerRed';
import { PaymentContext } from './PaymentContext';
import styles from './styles.module.css';
import { Adyen } from './Forms';
import { AdyenConfig } from './types';
import { Button } from '../../Shared/Button';
import { css } from 'styled-components/macro';
import { TermsAndConditions } from './Terms';
import { Logo } from 'V2/Shared/Logo';
import { Lock } from 'V2/Shared/Icons/Lock';
import { getAdyenConfig } from './getAdyenConfig';
import { useAppDetails, useRate } from 'V2/Api/BindAPIHooks';
import { capitalize } from 'lodash';
import { PaymentPlanSummary } from '../PaymentPlanSummary';

export const PaymentDetails: React.FC<StepType> = ({
  fields,
  isSubmitting: isAnimating,
  status: {
    id: stepName,
    field_data: fieldData,
    insurance_application_id: insuranceApplicationId,
  },
  summary: { entities, commands },
  config,
}) => {
  const {
    options_uris: optionsUris,
    application_focus: {
      path: { identifier },
    },
    features_by_key: {
      signature_summaries: signatureSummaries,
      predictable_protection_fee_applicable: predictableProtectionFeeApplicable,
    },
  } = config;

  const {
    adyen,
    formLoaded,
    setFields,
    isDeactivating,
    setIsDeactivating,
    setFormValid,
    setFormLoaded,
    isSubmitting,
    setIsSubmitting,
  } = useContext(PaymentContext);

  const { getResource, error: apiError, setError: setApiError } = useApi();

  const {
    app: { summary },
  } = useApplyProvider();

  const { update } = useUpdateEntityStatus();

  const {
    data: rate,
    fetch: fetchRate,
    isLoading: isLoadingRate,
  } = useRate({ appId: insuranceApplicationId });

  const { data: detailsData, fetch: fetchDetails } = useAppDetails({
    appId: insuranceApplicationId,
  });

  const [errors, setErrors] = useState<string[]>([]);

  const closeError = useCallback((errorIndex: number) => {
    setErrors((prevErrors) =>
      prevErrors.filter((_, index) => index !== errorIndex)
    );
  }, []);

  // payment_plan
  const paymentPlan = entities.find(
    (e) => e.type.id === 'payment_plan' && e.parent_entity_id === identifier
  );
  const paymentPlanId = paymentPlan ? paymentPlan.id : null;

  // we know this driver exists to pull out primary driver for cardholder name;
  const primaryDriver = summary.entities
    .filter((e) => e.type.id === 'driver')
    .find((e) => e.type.sub_type === 'named_insured')!;

  const signaturesRequired =
    fields.filter((f) => f.entity_type.id === 'signature').length > 0;

  useEffect(() => {
    setIsDeactivating(false);
    setFormLoaded(false);
    setFormValid(false);
  }, [setIsDeactivating, setFormValid, setFormLoaded]);

  const {
    payment_method: paymentMethodField,
    payment_plan_id: paymentPlanIdField,
    payment_token: paymentTokenField,
  } = useFields(fields, ['payment_method', 'payment_plan_id', 'payment_token']);
  const paymentMethodChosen: string = `${paymentMethodField.value}`;

  useEffect(() => {
    setErrors(paymentTokenField.errors);
  }, [paymentTokenField.errors]);

  const tokenUri: string = optionsUris.find(
    (o: OptionUriType) => o.field_id === 'payment_token'
  )!.uri;
  const [paymentConfig, setPaymentConfig] =
    useState<Nullable<AdyenConfig | any>>(null); // fix any

  useEffect(() => {
    if (insuranceApplicationId) {
      fetchDetails();
    }
  }, [insuranceApplicationId, fetchDetails]);

  const postalCode =
    Object.values(detailsData?.address ?? {})[0]?.zip?.value ?? '';

  // Due to how Adyen unloads from the DOM
  // It can cause errors if we don't do this
  useEffect(() => {
    if (stepName === 'payment_details') {
      setFields(() => fieldData);
    }
  }, [setFields, fieldData, stepName]);

  useEffect(() => {
    if (!isAnimating && !isSubmitting) {
      // If AVS is enabled (partial/full), wait until we have a postalCode
      // before creating the Adyen configuration. This ensures the ZIP field
      // is prefilled by Adyen and avoids remounting the checkout later.
      const requiresAvs = env.adyenCardAvsMode !== 'none';
      if (requiresAvs && !postalCode) return;

      if (tokenUri && paymentPlanIdField && !paymentConfig) {
        getResource({ url: tokenUri }).then(({ data }) => {
          setPaymentConfig(
            getAdyenConfig({
              paymentMethod: paymentMethodChosen,
              environment: env.adyenEnvironment,
              formData: {
                holderName: primaryDriver.label,
                postalCode: postalCode,
              },
            })
          );
        });
      }
    }
  }, [
    paymentPlanId,
    paymentMethodChosen,
    optionsUris,
    isAnimating,
    isSubmitting,
    getResource,
    paymentPlanIdField,
    tokenUri,
    paymentConfig,
    primaryDriver.label,
    postalCode,
  ]);

  const goToQuoteSummary = useCallback(() => {
    const moveToQuoteSummaryCommand = commands.find(
      (command) => command.id === 'move_to_requote'
    );
    if (moveToQuoteSummaryCommand) {
      setIsDeactivating(true);
      update(moveToQuoteSummaryCommand);
    } else {
      // Black bar error?
    }
  }, [commands, setIsDeactivating, update]);

  const paymentForm = useMemo(() => {
    // Process One payments are handled in a modal opened by clicking Checkout
    return paymentConfig && paymentPlanIdField ? (
      <div className={styles.purchaseSummaryTitleContainer}>
        <Adyen fields={fields} config={paymentConfig} />
      </div>
    ) : (
      <></>
    );
  }, [paymentConfig, paymentPlanIdField, fields]);

  const formProps = useMemo(() => {
    let initialValues = {};
    let validationSchema: Yup.ObjectSchema<ObjectShape> = Yup.object().shape(
      {}
    );
    const summaries = signatureSummaries as SignatureSummaryType[];

    if (signaturesRequired) {
      validationSchema = Yup.object().shape(
        summaries.reduce(
          (values, document) => ({
            ...values,
            [document.group_id]: Yup.boolean().oneOf([true], 'Required'),
          }),
          {}
        )
      );

      initialValues = summaries.reduce(
        (formValues, document) => ({
          ...formValues,
          [document.group_id]: false,
        }),
        {}
      );
    }

    return {
      validationSchema,
      initialValues,
      onSubmit: () => {},
      enableReinitialize: true,
      validateOnMount: true,
    };
  }, [signatureSummaries, signaturesRequired]);

  useEffect(() => {
    if (insuranceApplicationId) {
      fetchRate();
    }
  }, [insuranceApplicationId, fetchRate]);

  const paymentDetails = rate?.paymentDetails;
  const selectedPlanId = paymentDetails?.selectedPaymentPlan?.identifier;
  const selectedPlan = paymentDetails?.paymentPlans?.find(
    (plan) => plan.identifier === selectedPlanId
  );

  const paymentDaysOfMonth = selectedPlan?.paymentDaysOfMonth ?? [];
  const paymentFrequencyIdentifier = selectedPlan?.paymentFrequencyIdentifier;
  const isFlexiblePaymentPlan = selectedPlan?.paymentPlanEnum === 'flexible';

  const isPayInFullPlan =
    paymentFrequencyIdentifier === 'full_pay' ||
    paymentFrequencyIdentifier === 'in_full';

  const paymentDayOfWeek = capitalize(
    selectedPlan?.paymentDaysOfWeek?.[0] ?? ''
  );

  const getOrdinal = (n: number) => {
    if (n === 1 || n === 21 || n === 31) return `${n}st`;
    if (n === 2 || n === 22) return `${n}nd`;
    if (n === 3 || n === 23) return `${n}rd`;
    return `${n}th`;
  };

  const getPaymentDaysOfMonthLabel = () => {
    if (paymentDaysOfMonth.length === 1) {
      return getOrdinal(paymentDaysOfMonth[0]);
    } else if (paymentDaysOfMonth.length === 2) {
      return `${getOrdinal(paymentDaysOfMonth[0])} and ${getOrdinal(
        paymentDaysOfMonth[1]
      )}`;
    } else {
      return '';
    }
  };

  const paymentsMadeOn =
    !isPayInFullPlan && isFlexiblePaymentPlan
      ? ['monthly', 'semi_monthly'].includes(paymentFrequencyIdentifier ?? '')
        ? getPaymentDaysOfMonthLabel()
        : paymentDayOfWeek
      : undefined;

  const futurePaymentAmount = () =>
    isPayInFullPlan ? undefined : selectedPlan?.installmentAmount;

  const paymentFrequency = () =>
    isPayInFullPlan
      ? 'Pay in full'
      : capitalize(selectedPlan?.paymentFrequencyLabel ?? '');

  const dueToday = isPayInFullPlan
    ? undefined
    : selectedPlan?.downPaymentAmount;

  const totalAmount = selectedPlan?.totalAmount;

  return (
    <div
      className={
        styles.payment + ' ' + (errors.length > 0 ? styles.withError : '')
      }
    >
      {!isDeactivating &&
        errors.map((error, i) => (
          <div key={i} className={styles.errorBanner}>
            <ErrorBannerRed
              data-testid="payment-error-message"
              onClose={() => closeError(i)}
            >
              {error}
            </ErrorBannerRed>
          </div>
        ))}
      <div className={styles.navigation}>
        <div className={styles.logo}>
          <Logo useImageTag />
        </div>
      </div>
      <Formik {...formProps}>
        {({ isValid }) => (
          <div className={styles.main}>
            <div className={styles.title}>Complete your purchase</div>
            {isLoadingRate || !selectedPlan ? (
              <div className={styles.loader}>
                <Spinner color={css`var(--brand-01)`} />
              </div>
            ) : (
              <PaymentPlanSummary
                dueToday={dueToday}
                futurePaymentAmount={futurePaymentAmount()}
                paymentFrequency={paymentFrequency()}
                paymentsMadeOn={paymentsMadeOn}
                totalCost={totalAmount || ''}
                paymentPlan={selectedPlan}
                showFeeBreakdown={!!predictableProtectionFeeApplicable}
                goToQuoteSummary={goToQuoteSummary}
              />
            )}
            {paymentForm}
            {(!formLoaded || isDeactivating) && (
              <div className={styles.loader}>
                <Spinner color={css`var(--brand-01)`} />
              </div>
            )}

            {signaturesRequired && (
              <TermsAndConditions
                valid={isDeactivating || isValid}
                fields={fields}
                config={config}
              />
            )}
            <div
              data-testid="actions"
              className={[styles.action, styles.mtop28].join(' ')}
            >
              <Button
                disabled={isDeactivating}
                width="fullWidth"
                size="lg"
                isSubmitting={isSubmitting}
                data-testid="check-out-button"
                onClick={() => {
                  if (apiError) setApiError(null);

                  // adyen credit card and ach submit
                  if (adyen && paymentPlanIdField) {
                    setIsSubmitting(true);

                    if (isValid) {
                      adyen.submit();
                      !adyen.isValid &&
                        setTimeout(() => setIsSubmitting(false), 0);
                    } else {
                      // Due to the way react works, we need to add
                      // "setIsSubmitting(false)" to the event loop
                      // using a setTimeout: otherwise it will cancel
                      // out the "setIsSubmitting(true)" completely.
                      setTimeout(() => setIsSubmitting(false), 0);
                    }
                  }
                }}
                type="button"
              >
                <span>
                  <Lock />
                  {'  '}
                  Check Out Securely
                </span>
              </Button>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};
